/**
 * useFavorites — modulet e preferuara të sidebar-it për përdoruesin aktual.
 *
 * Burimi i së vërtetës është backend-i (GET/PUT /api/me/favorites) që i
 * sinkronizon mes pajisjeve. localStorage përdoret vetëm si cache për
 * paraqitje të menjëhershme në ngarkim. Komponentët që duan sync në kohë reale
 * dëgjojnë eventin `nexuma:favoritesChanged`.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import configService from '../services/configService';

const STORAGE_KEY = 'nexuma_favorites';

function readCache() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter(x => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function writeCache(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    window.dispatchEvent(new Event('nexuma:favoritesChanged'));
  } catch { /* ignore */ }
}

/** Returns { favorites, isFavorite, toggleFavorite, refresh } */
export default function useFavorites() {
  const [favorites, setFavorites] = useState(readCache);
  // Set as soon as the user stars/unstars. Once dirty, a slow in-flight backend
  // load is never allowed to overwrite local state — so a freshly added favorite
  // can't vanish on its own. Only un-starring removes a favorite.
  const dirtyRef = useRef(false);
  // Guard against setState after the component unmounts (e.g. the sidebar Drawer
  // closes) while a backend load is still in flight — avoids the React memory-leak warning.
  const isMountedRef = useRef(true);
  useEffect(() => () => { isMountedRef.current = false; }, []);

  // Load authoritative list from backend on mount
  const refresh = useCallback(async () => {
    try {
      const r = await configService.getFavorites();
      if (dirtyRef.current || !isMountedRef.current) return; // user changed it, or we unmounted — keep local
      if (r?.success && Array.isArray(r.data)) {
        const list = r.data.filter(x => typeof x === 'string');
        setFavorites(list);
        writeCache(list);
      }
    } catch { /* keep cached list */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Re-sync when another component on the page changes favorites
  useEffect(() => {
    const sync = () => setFavorites(readCache());
    window.addEventListener('nexuma:favoritesChanged', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('nexuma:favoritesChanged', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const isFavorite = useCallback((id) => favorites.includes(id), [favorites]);

  const toggleFavorite = useCallback((id) => {
    if (!id) return;
    dirtyRef.current = true;
    setFavorites((prev) => {
      const next = prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id];
      writeCache(next);                                   // optimistic + broadcast
      configService.saveFavorites(next).catch(() => {});  // persist (best-effort)
      return next;
    });
  }, []);

  return { favorites, isFavorite, toggleFavorite, refresh };
}
